/**
 * Journaux du calcul d'une simulation.
 *
 * Sans eux, une simulation échouée n'affiche qu'une ligne — « Simulation
 * failed » — et la seule façon d'en savoir plus est la console AWS, que les
 * personnes qui lancent des simulations n'ont pas. C'est ce qui les oblige
 * aujourd'hui à demander de l'aide pour chaque échec.
 *
 * Les journaux ne sont chargés qu'au dépliage : ils sont volumineux, et la
 * plupart des consultations ne les demandent pas.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/authService';

/**
 * Traduit la raison d'échec renvoyée par AWS.
 *
 * Ces messages sont en anglais, techniques, et n'indiquent pas quoi faire.
 * Les trois premiers couvrent la quasi-totalité des échecs réels, et chacun
 * appelle une action différente : corriger le manifeste, relancer, ou lire
 * les journaux. Sans cette traduction, les trois se ressemblent.
 */
const CLE_PAR_MOTIF = [
  [/outofmemory|memory usage/, 'memoire'],
  [/host ec2 instance terminated|spot/, 'spot'],
  [/cannotpull|image/, 'image'],
  [/exited/, 'sortie'],
];

/**
 * Reconnaît la raison d'échec renvoyée par AWS et rend la clé de traduction
 * correspondante.
 *
 * Ces messages sont en anglais, techniques, et n'indiquent pas quoi faire. Les
 * trois premiers couvrent la quasi-totalité des échecs réels, et chacun appelle
 * une action différente : corriger le manifeste, relancer, ou lire les
 * journaux. Sans cette reconnaissance, les trois se ressemblent.
 */
const cleDuMotif = (motif) => {
  if (!motif) return null;
  const m = motif.toLowerCase();
  const trouve = CLE_PAR_MOTIF.find(([regex]) => regex.test(m));
  return trouve ? trouve[1] : null;
};

const JournauxCalcul = ({ simulationId, statut }) => {
  const { t, i18n } = useTranslation();
  const [ouvert, setOuvert] = useState(false);
  const [lignes, setLignes] = useState(null);
  const [motif, setMotif] = useState(null);
  const [statutJob, setStatutJob] = useState(null);
  const [message, setMessage] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState(null);

  const basculer = async () => {
    if (ouvert) {
      setOuvert(false);
      return;
    }
    setOuvert(true);

    // Déjà chargés : on ne rappelle pas l'API à chaque ouverture.
    if (lignes !== null || message !== null) return;

    setChargement(true);
    setErreur(null);
    try {
      const { data } = await api.get(
        `/api/simulations/detail/${simulationId}/logs/?lignes=300`
      );
      setLignes(data.lignes || []);
      setMotif(data.motif || null);
      setStatutJob(data.statut || null);
      setMessage(data.message || null);
    } catch (err) {
      setErreur(
        err.response?.data?.message
        || t('journaux.echec')
      );
    } finally {
      setChargement(false);
    }
  };

  // AWS renseigne statusReason même sur un calcul réussi : un job qui s'est
  // terminé normalement porte « Essential container in task exited ». Interpréter
  // ce motif sans vérifier l'échec ferait annoncer une erreur sur une simulation
  // qui a parfaitement marché.
  const echec = statutJob === 'FAILED' || statut === 'failed';
  const cle = echec ? cleDuMotif(motif) : null;

  return (
    <div className="mt-4">
      <button
        onClick={basculer}
        aria-expanded={ouvert}
        className="flex items-center gap-2 text-sm text-dark-300 hover:text-dark-100 transition-colors"
      >
        <svg
          className={`w-4 h-4 transition-transform ${ouvert ? 'rotate-90' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        {ouvert ? t('journaux.masquer') : t('journaux.voir')}
        {statut === 'failed' && !ouvert && (
          <span className="text-error-400 text-xs">
            {t('journaux.indice')}
          </span>
        )}
      </button>

      {ouvert && (
        <div className="mt-3 space-y-3">
          {chargement && (
            <div className="flex items-center gap-2 text-dark-400 text-sm p-4">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-dark-400" />
              {t('journaux.recuperation')}
            </div>
          )}

          {erreur && (
            <div className="bg-warning-500/10 border border-warning-500/30 rounded-lg p-4">
              <p className="text-warning-400 text-sm">{erreur}</p>
            </div>
          )}

          {!chargement && !erreur && cle && (
            <div className="bg-error-500/10 border border-error-500/30 rounded-lg p-4">
              <p className="text-error-400 font-medium text-sm">{t(`journaux.${cle}.titre`)}</p>
              <p className="text-dark-300 text-sm mt-1">{t(`journaux.${cle}.detail`)}</p>
              {i18n.exists(`journaux.${cle}.action`) && (
                <p className="text-dark-200 text-sm mt-2">
                  <span className="text-dark-400">{t('journaux.aFaire')}</span>
                  {t(`journaux.${cle}.action`)}
                </p>
              )}
            </div>
          )}

          {!chargement && !erreur && echec && motif && (
            <p className="text-dark-500 text-xs font-mono break-all">
              {t('journaux.raisonAws', { motif })}
            </p>
          )}

          {!chargement && !erreur && message && (
            <div className="bg-dark-900 rounded-lg p-4">
              <p className="text-dark-400 text-sm">{message}</p>
            </div>
          )}

          {!chargement && !erreur && lignes && lignes.length > 0 && (
            <div>
              <pre className="bg-dark-900 rounded-lg p-4 overflow-x-auto text-xs text-dark-200 font-mono max-h-96 overflow-y-auto whitespace-pre-wrap">
                {lignes.join('\n')}
              </pre>
              <p className="text-dark-500 text-xs mt-2">
                {t('journaux.dernieresLignes', { n: lignes.length })}
              </p>
            </div>
          )}

          {!chargement && !erreur && lignes && lignes.length === 0 && !message && (
            <div className="bg-dark-900 rounded-lg p-4">
              <p className="text-dark-400 text-sm">
                {t('journaux.vide')}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default JournauxCalcul;
